import { Component, OnInit } from '@angular/core';
import { UserDataService } from '../../../shared/services/userData.service';
import { EopDocumentService } from '../../../shared/services/eopDocument.service';
import { IEopDocument } from '../../../shared/models/eopDocument';
import { IntroductionComponent } from "../../components/introduction/introduction.component";
import { PopupMessageComponent } from "../../components/popup-message/popup-message.component";
import { SpinnerComponent } from '../../components/spinner/spinner.component';
import { CommonModule } from '@angular/common';
import { EopAssistantComponent } from '../../components/eop-assistant/eop-assistant.component';
import { Icons } from '../../../shared/helpers/icons';
import { LucideAngularModule } from 'lucide-angular';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ESectionRoutes, ESectionTypes, ESubSectionTypes, getSectionNameFromRoute } from '../../../shared/models/eop_types.enum';
import { RecordOfChangesComponent } from './record-of-changes/record-of-changes.component';
import { RecordOfDistributionComponent } from './record-of-distribution/record-of-distribution.component';
import { EopSectionService } from '../../../shared/services/eopSection.service';
import { TrainingExerciseComponent } from './training-exercise/training-exercise.component';
import { EopNavigationService } from '../../../shared/services/eopNavigation.service';
import { PrvNextButtonsComponent } from '../../components/prv-next-buttons/prv-next-buttons.component';
import { IUserInfo } from '../../../shared/models/contact';
import { NoEmojiDirective } from '../../../shared/helpers/noEmoji.directive';

@Component({
  selector: 'basic-plan-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    LucideAngularModule,
    IntroductionComponent, 
    PopupMessageComponent,
    SpinnerComponent,
    EopAssistantComponent,
    RecordOfChangesComponent,
    RecordOfDistributionComponent,
    EopAssistantComponent,
    TrainingExerciseComponent,
    PrvNextButtonsComponent,
    NoEmojiDirective
  ],
  templateUrl: './basic-plan-page.component.html',
  styleUrl: './basic-plan-page.component.css'
})
export class BasicPlanPageComponent implements OnInit{
  
  icons = Icons;
  currentBuildingId: string | null = null;
  currentUser: IUserInfo | null = null;
  currentPageRoute: string | null = null;
  eopDoc: IEopDocument | null = null;
  sectionTypeId: number | null = null;
  subSectionTypeId: number | null = null;
  pageTitle: string = '';
  description: string = '';
  
  subSectionsMap: { [key: string]: string[] } = {
    'introductory-materials': [
      'Promulgation',
      'Approval Implementation',
      'Record of Changes',
      'Record of Distribution'
    ],
    'purpose-situation-overview': [
      'Purpose',
      'Scope',
      'Situation Overview',
      'Planning Assumptions'
    ]
  };
  
  documents: string[] = [];
  selectedDoc: string | null = null;
  
  loading: boolean = false;
  messageModal: string | null = null;
  popupMessage: string | null = null;
  

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private userDataService: UserDataService,
    private eopDocumentService: EopDocumentService,
    private eopSeectionService: EopSectionService,
    public eopNavigationService: EopNavigationService
  ) {}

  ngOnInit(): void {
    this.currentBuildingId = this.userDataService.getBuildingId();
    this.currentUser = this.userDataService.getUserData();
    this.route.paramMap.subscribe(params => {
      this.currentPageRoute = params.get('pageRoute');
      if (this.currentPageRoute) {
        this.verifyPageExists();
        this.pageTitle = getSectionNameFromRoute(this.currentPageRoute)?.toUpperCase() || 'N/A';
        this.sectionTypeId = ESectionRoutes[this.currentPageRoute as keyof typeof ESectionRoutes];
        // Load subsections if needed
        const subSections = this.subSectionsMap[this.currentPageRoute];
        if (subSections) {
          this.documents = subSections;
          this.selectedDoc = subSections[0]; // Default to first subsection
          this.subSectionTypeId = ESubSectionTypes[this.selectedDoc as keyof typeof ESubSectionTypes];
        } else {
          this.documents = []; 
          this.selectedDoc = null;
          this.subSectionTypeId = null;
        }
        this.loadEopDoc();
      }
    });
  }

  // make api call to verify if page exists for the building's plan, because users can apply multiple pages to their plan
  // if not, redirect to not-found page
  verifyPageExists(): void {
    if (this.currentBuildingId && this.currentPageRoute) {
      console.log(ESectionRoutes[this.currentPageRoute as keyof typeof ESectionRoutes]);
      this.eopSeectionService.verifyIfSectionExistsOnnBuilding(this.currentBuildingId, ESectionRoutes[this.currentPageRoute as keyof typeof ESectionRoutes]).subscribe({
        next: (response) => {
          if (response.has_error) {
            this.router.navigate(['/not-found']);
            
          }
        },
        error: (error) => {
          console.log('Error:', error.message);
          this.router.navigate(['/not-found']);
        }
      });
    }
  }

  loadEopDoc(): void {
    if (this.currentPageRoute == 'training-exercises' ||
      this.subSectionTypeId == ESubSectionTypes['Record of Changes'] ||
      this.subSectionTypeId == ESubSectionTypes['Record of Distribution']
    ) {
      return;
    }
    if (this.currentBuildingId && this.currentPageRoute && this.sectionTypeId) {
      this.loading = true;
      this.eopDocumentService.getEopDocument(
        this.currentBuildingId, 
        this.sectionTypeId,
        this.subSectionTypeId ?? undefined
      ).subscribe({
        next: (response: any) => {
          if (!response.has_error) {
            this.eopDoc = response.data ? response.data : null;
            console.table(this.eopDoc);
            this.description = this.eopDoc?.description || '';
          }
          this.loading = false;
        },
        error: (error: any) => {
          this.loading = false;
          console.error('Error:', error.message);
        }
      })
    } else return;
  }

  onSubmit(): void {
    if (!this.description.trim()) {
      return;
    }
    if (this.currentBuildingId && this.currentPageRoute && this.sectionTypeId) {
      const updateData = { description: this.description };
    
      this.loading = true;
      this.eopDocumentService.updateEopDocument(
        this.currentBuildingId, 
        updateData,
        this.sectionTypeId,
        this.subSectionTypeId ?? undefined
      ).subscribe({
        next: (response) => {
          this.loading = false;
          if (!response.has_error) {
            this.messageModal = 'success';
            this.popupMessage = 'This occurred after updating an eop document.';
            this.loadEopDoc();
          }
        },
        error: (error) => {
          this.loading = false;
          this.messageModal = 'unsuccess';
          this.popupMessage = error.message;
        }
      });

    }
  }
  

  handleApprovePromulgation(): void {
    if (this.currentBuildingId && this.currentPageRoute && this.sectionTypeId) {
        this.loading = true;
        this.eopDocumentService.approveEopDocument(
          this.currentBuildingId,
          this.sectionTypeId,
          this.subSectionTypeId ?? undefined
        ).subscribe({
          next: (response) => {
            this.loading = false;
            if (!response.has_error) {
              this.messageModal = 'success';
              this.popupMessage = 'This occurred after approving an eop document.';
              this.loadEopDoc();
            } else {
              this.messageModal = 'unsuccess';
              this.popupMessage = response.message || 'An error occurred while approving the document.';
            }
          },
          error: (error) => {
            this.loading = false;
            this.messageModal = "unsuccess";
            this.popupMessage = error.message;
          }
        })
    }
  }



  selectDoc(doc: string) {
    this.selectedDoc = doc;
    this.subSectionTypeId = ESubSectionTypes[doc as keyof typeof ESubSectionTypes];
    this.loadEopDoc();
  }
  

  resetMessageModal(): void {
    this.messageModal = null;
    this.popupMessage = null;
  }
  
  canSave(): boolean {
    if (!this.currentUser) return false;
    return this.currentUser.can_edit || this.currentUser.can_admin;
  }

  get isContentUnchanged(): boolean {
    return this.description.trim() === this.eopDoc?.description?.trim();
  }
}
